import { io } from 'socket.io-client';
import { getAuthToken } from './api';

const IDLE_DISCONNECT_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'];
const RECONNECTION_ATTEMPTS = 8;
const RECONNECTION_DELAY_MS = 1000;
const RECONNECTION_DELAY_MAX_MS = 10000;

let socket;
let onNotificationHandler = null;
let onResumeHandler = null;
let idleTimer = null;
let hiddenTimer = null;
let presenceBound = false;
let suspended = false;
let generation = 0;

function attachSocketListeners() {
    if (!socket) {
        return;
    }
    socket.off('admin_notification:new', onAdminNotification);
    socket.on('admin_notification:new', onAdminNotification);
    socket.off('disconnect', onServerDisconnect);
    socket.on('disconnect', onServerDisconnect);
}

function onAdminNotification(payload) {
    if (typeof onNotificationHandler === 'function') {
        onNotificationHandler(payload);
    }
}

function clearPresenceTimers() {
    clearTimeout(idleTimer);
    clearTimeout(hiddenTimer);
    idleTimer = null;
    hiddenTimer = null;
}

function closeSocket() {
    if (!socket) {
        return;
    }
    socket.removeAllListeners();
    socket.disconnect();
    socket = undefined;
}

function suspendForIdle() {
    suspended = true;
    clearPresenceTimers();
    closeSocket();
}

function scheduleIdleDisconnect() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(suspendForIdle, IDLE_DISCONNECT_MS);
}

function scheduleHiddenDisconnect() {
    clearTimeout(hiddenTimer);
    hiddenTimer = setTimeout(suspendForIdle, IDLE_DISCONNECT_MS);
}

async function openSocket() {
    const open_generation = generation;
    const token = await getAuthToken();
    if (!token || open_generation !== generation) {
        return false;
    }
    closeSocket();
    socket = io({
        path: '/socket.io',
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: RECONNECTION_ATTEMPTS,
        reconnectionDelay: RECONNECTION_DELAY_MS,
        reconnectionDelayMax: RECONNECTION_DELAY_MAX_MS,
        timeout: 20000,
    });
    attachSocketListeners();
    scheduleIdleDisconnect();
    return true;
}

function onServerDisconnect(reason) {
    if (reason !== 'io server disconnect') {
        return;
    }
    suspended = true;
    clearPresenceTimers();
    if (socket) {
        socket.removeAllListeners();
        socket = undefined;
    }
}

async function resumeFromIdle() {
    clearTimeout(hiddenTimer);
    hiddenTimer = null;
    if (!suspended) {
        scheduleIdleDisconnect();
        return;
    }
    const resume_generation = generation;
    suspended = false;
    try {
        const opened = await openSocket();
        if (resume_generation !== generation) {
            return;
        }
        if (!opened) {
            suspended = true;
            return;
        }
        if (typeof onResumeHandler === 'function') {
            onResumeHandler();
        }
    } catch (error) {
        if (resume_generation === generation) {
            suspended = true;
        }
        console.error(error);
    }
}

function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
        scheduleHiddenDisconnect();
        return;
    }
    clearTimeout(hiddenTimer);
    hiddenTimer = null;
    void resumeFromIdle();
}

function onUserActivity() {
    if (document.visibilityState === 'hidden') {
        return;
    }
    void resumeFromIdle();
}

function bindPresence() {
    if (presenceBound || typeof document === 'undefined') {
        return;
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    for (const event_name of ACTIVITY_EVENTS) {
        window.addEventListener(event_name, onUserActivity);
    }
    presenceBound = true;
    scheduleIdleDisconnect();
}

function unbindPresence() {
    if (!presenceBound || typeof document === 'undefined') {
        return;
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
    for (const event_name of ACTIVITY_EVENTS) {
        window.removeEventListener(event_name, onUserActivity);
    }
    presenceBound = false;
    clearPresenceTimers();
}

export async function connectAdminNotificationsSocket(onNotification, hooks = {}) {
    disconnectAdminNotificationsSocket();
    onNotificationHandler = onNotification;
    onResumeHandler = typeof hooks.onResume === 'function' ? hooks.onResume : null;
    const opened = await openSocket();
    if (!opened) {
        return;
    }
    bindPresence();
}

export function disconnectAdminNotificationsSocket() {
    generation += 1;
    suspended = false;
    onResumeHandler = null;
    onNotificationHandler = null;
    unbindPresence();
    closeSocket();
}
